/**
 * AppErrorBoundary — catches render-time React errors and displays a
 * friendly recovery screen instead of a blank white page.
 *
 * Wraps the entire application tree in main.jsx.
 * Dynamic sub-routes can add their own nested <RouteErrorBoundary> for
 * more granular recovery (see OfflineFallbackCard for non-fatal cases).
 *
 * @module components/ui/AppErrorBoundary
 */

import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { TriangleAlert, RotateCcw } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from './index.js';

function ErrorFallback({ error, resetErrorBoundary }) {
  const message = error?.message || 'An unexpected error occurred. Please try again.';

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,248,237,0.95),_rgba(250,244,236,0.9)_38%,_rgba(242,236,229,1)_100%)] px-4 py-10 flex items-center justify-center">
      <Card elevated className="w-full max-w-lg border-pearl-300/80 bg-white/95 backdrop-blur">
        <CardHeader className="items-center text-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-50 text-amber-500 ring-1 ring-amber-100">
            <TriangleAlert size={34} strokeWidth={1.8} />
          </div>
          <div className="space-y-2">
            <CardTitle className="font-serif text-3xl text-primary-900">
              Something went wrong
            </CardTitle>
            <CardDescription className="text-base leading-7 text-ink-600">
              The page hit an unexpected error. You can try again, or reload the page if the problem persists.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent className="space-y-6 text-center">
          <div className="rounded-2xl border border-pearl-300 bg-pearl-50 px-4 py-3 text-sm leading-6 text-ink-700">
            {message}
          </div>

          <p className="text-sm leading-6 text-ink-500">
            If you are offline, reconnect and try again. Your unsaved work may have been preserved in the local cache.
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              type="button"
              onClick={resetErrorBoundary}
              className="h-11 px-6 rounded-2xl"
            >
              <RotateCcw size={16} />
              Try Again
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="h-11 px-6 rounded-2xl"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function AppErrorBoundary({ children }) {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => window.location.reload()}
      onError={(error, info) =>
        console.error('[AppErrorBoundary]', error, info)
      }
    >
      {children}
    </ErrorBoundary>
  );
}
